import { DashboardEvent, LookerEmbedSDK } from '@looker/embed-sdk';
import * as React from 'react';

import { useSetSearch } from 'common/hooks/useSetSearch';

import { analyticsDashboardRoute } from '.';
import { useDashboardState } from './dashboardState';
import { getDashboardParams, LOOKER_ORIGIN } from './useEmbedUrl';

const useDashboardEmbed = (embedUrl: string) => {
  const [dashboardState, setDashboardState] = useDashboardState();
  const [currentEmbedUrl, setCurrentEmbedUrl] = React.useState<string | undefined>(undefined);
  const timeout = React.useRef<number>(null);
  const setSearch = useSetSearch(analyticsDashboardRoute);

  const checkOverdue = React.useCallback(() => {
    if (dashboardState !== 'success') {
      setDashboardState('overdue');
    }
  }, [setDashboardState, dashboardState]);

  React.useEffect(() => {
    if (currentEmbedUrl !== embedUrl) {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = window.setTimeout(() => {
        checkOverdue();
      }, 60_000);
      setCurrentEmbedUrl(embedUrl);
    }
  }, [embedUrl, currentEmbedUrl, checkOverdue]);

  const setDashboardParams = (event: DashboardEvent) => {
    if (event.dashboard && event.dashboard.url) {
      const dashboardParams = getDashboardParams();
      const params = event.dashboard.url.split('?')[1] ?? '';
      if (!params && !dashboardParams) return;
      if (params !== dashboardParams) {
        setSearch({ dashboardParams: btoa(params) }, true);
      }
    }
  };

  const createEmbedRef = React.useCallback(
    (el: HTMLDivElement) => {
      setDashboardState('none');
      if (embedUrl && el) {
        setDashboardState('loading');
        LookerEmbedSDK.init(LOOKER_ORIGIN);
        el.innerHTML = '';
        const dash = LookerEmbedSDK.createDashboardWithUrl(embedUrl);
        dash
          .withAllowAttr('publickey-credentials-get *')
          .withClassName('dashboard-embed')
          .withSandboxAttr(
            'allow-same-origin',
            'allow-scripts',
            'allow-popups',
            'allow-forms',
            'allow-downloads',
          )
          .appendTo(el)
          // events below are only sent/received via postMessage from iframe if FT domains are whitelisted in the Looker admin panel
          // and the same origin is used in the embed_domain parameter of the iframe src
          .on('dashboard:run:start', setDashboardParams)
          .on('dashboard:filters:changed', setDashboardParams)
          .on('dashboard:run:complete', () => setDashboardState('success'))
          .build()
          .connect()
          .catch((error: Error) => {
            console.error('failed to load dashboard', error);
            setDashboardState('failure');
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [embedUrl],
  );

  return createEmbedRef;
};

export default useDashboardEmbed;
